/* eslint-disable */

const RISK_PROFILES = [
  {
    id: 'very-high',
    label: 'Very High Risk',
    shortLabel: 'V. High',
    min: 0,
    max: 10,
    color: '#DC2626',
    description:
      'Very high risk capacity and tolerance. Comfortable with significant market volatility in pursuit of long-term wealth creation.',
  },
  {
    id: 'high',
    label: 'High Risk',
    shortLabel: 'High',
    min: 10,
    max: 40,
    color: '#EA580C',
    description:
      'High risk capacity and tolerance. Comfortable taking market risk for higher long-term returns.',
  },
  {
    id: 'medium',
    label: 'Medium Risk',
    shortLabel: 'Medium',
    min: 40,
    max: 70,
    color: '#CA8A04',
    description:
      'Moderate risk capacity and tolerance. Comfortable with a balanced mix of growth and stability.',
  },
  {
    id: 'low',
    label: 'Low Risk',
    shortLabel: 'Low',
    min: 70,
    max: 90,
    color: '#65A30D',
    description:
      'Low risk capacity and tolerance. Prefers stability with limited exposure to market fluctuations.',
  },
  {
    id: 'very-low',
    label: 'Very Low Risk',
    shortLabel: 'V. Low',
    min: 90,
    max: 100,
    color: '#15803D',
    description:
      'Very low risk capacity and tolerance. Focuses primarily on capital preservation.',
  },
];

export const getRiskProfile = (debtCashAllocation) => {
  const allocation = Math.max(0, Math.min(100, Number(debtCashAllocation) || 0));

  // Walk bands left→right; last band inclusive at 100
  for (let i = 0; i < RISK_PROFILES.length; i++) {
    const profile = RISK_PROFILES[i];
    const isLast = i === RISK_PROFILES.length - 1;
    if (allocation >= profile.min && (isLast ? allocation <= profile.max : allocation < profile.max)) {
      return { ...profile, allocation };
    }
  }

  return { ...RISK_PROFILES[0], allocation };
};

const CustomerRiskProfile = ({ assets = [] }) => {
  const debtAllocation =
    assets?.find((asset) => asset.category === 'Debt')?.percentage ?? 0;

  const profile = getRiskProfile(debtAllocation);
  // Map debt % directly onto the meter (0% = very high risk, 100% = very low risk)
  const markerPosition = Math.max(1.5, Math.min(98.5, Number(debtAllocation) || 0));

  return (
    <div className="mb-6 sm:mb-8 rounded-xl border border-gray-200 bg-gradient-to-br from-slate-50 via-white to-teal-50/40 px-4 py-4 sm:px-6 sm:py-5">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Risk Profile
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <h3
              className="text-lg sm:text-xl font-semibold"
              style={{ color: profile.color }}
            >
              {profile.label}
            </h3>
            <span
              className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium text-white"
              style={{ backgroundColor: profile.color }}
            >
              Debt {Number(debtAllocation).toFixed(1)}%
            </span>
          </div>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-gray-600">
            {profile.description}
          </p>
        </div>
      </div>

      <div className="mt-5 sm:mt-6">
        {/* Arrow marker */}
        <div className="relative mb-1.5 h-5">
          <div
            className="absolute top-0 flex -translate-x-1/2 flex-col items-center transition-all duration-500 ease-out"
            style={{ left: `${markerPosition}%` }}
          >
            <span
              className="mb-0.5 whitespace-nowrap rounded-md px-1.5 py-0.5 text-[10px] font-semibold text-white shadow-sm sm:text-xs"
              style={{ backgroundColor: profile.color }}
            >
              You
            </span>
            <svg
              width="12"
              height="8"
              viewBox="0 0 12 8"
              fill="none"
              aria-hidden="true"
            >
              <path d="M6 8L0 0H12L6 8Z" fill={profile.color} />
            </svg>
          </div>
        </div>

        {/* Segmented risk meter */}
        <div
          className="flex h-3 overflow-hidden rounded-full shadow-inner ring-1 ring-black/5 sm:h-3.5"
          role="meter"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Number(debtAllocation)}
          aria-label={`Risk profile: ${profile.label}`}
        >
          {RISK_PROFILES.map((band, index) => {
            const width = band.max - band.min;
            const isActive = band.id === profile.id;
            return (
              <div
                key={band.id}
                className="relative h-full transition-opacity duration-300"
                style={{
                  width: `${width}%`,
                  background: `linear-gradient(90deg, ${band.color}cc, ${band.color})`,
                  opacity: isActive ? 1 : 0.45,
                  boxShadow: isActive ? `inset 0 0 0 1px ${band.color}` : undefined,
                }}
                title={`${band.label} (${band.min}${index === RISK_PROFILES.length - 1 ? '–' : '–<'}${band.max}%)`}
              />
            );
          })}
        </div>

        {/* Scale labels — widths match risk bands */}
        <div className="mt-2 flex text-[10px] text-gray-500 sm:text-xs">
          {RISK_PROFILES.map((band) => (
            <span
              key={band.id}
              className={`text-center leading-tight ${band.id === profile.id ? 'font-semibold text-gray-800' : ''
                }`}
              style={{ width: `${band.max - band.min}%` }}
            >
              {band.shortLabel}
            </span>
          ))}
        </div>

        <p className="mt-3 text-xs text-gray-400">
          Derived from your Debt allocation. Higher debt share indicates a more conservative profile.
        </p>
      </div>
    </div>
  );
};

export default CustomerRiskProfile;
